import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  CardActionArea
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { formatScheduleSummary } from '../utils/dvlaSchedule';

interface DVLASettingsData {
  dvla_credits: number;
  dvla_check_frequency: string | null;
  next_dvla_check_date: string | null;
  dvla_automated_check_email: string | null;
  dvla_schedule_day_of_month: number | null;
  dvla_schedule_time: string | null;
  dvla_schedule_timezone: string | null;
}

const DVLASettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<DVLASettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingCredits, setPendingCredits] = useState(0);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/businesses/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        dvla_credits: response.data.dvla_credits || 0,
        dvla_check_frequency: response.data.dvla_check_frequency || null,
        next_dvla_check_date: response.data.next_dvla_check_date || null,
        dvla_automated_check_email: response.data.dvla_automated_check_email || null,
        dvla_schedule_day_of_month:
          response.data.dvla_schedule_day_of_month != null
            ? Number(response.data.dvla_schedule_day_of_month)
            : 1,
        dvla_schedule_time: response.data.dvla_schedule_time || null,
        dvla_schedule_timezone: response.data.dvla_schedule_timezone || 'Europe/London',
      });

      // Fetch pending requests to calculate reserved credits
      try {
        const pendingResponse = await axios.get(`${API_BASE_URL}/dvla-license-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingCredits(pendingResponse.data.length || 0);
      } catch (err) {
        console.error('Error fetching pending requests:', err);
      }
    } catch (err: any) {
      console.error('Error fetching DVLA settings:', err);
      setError(err.response?.data?.message || 'Failed to fetch DVLA settings');
    } finally {
      setLoading(false);
    }
  };

  const getFrequencyColor = (frequency: string | null): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!frequency) return 'default';
    return 'primary';
  };

  const scheduleSummary =
    settings?.dvla_check_frequency
      ? formatScheduleSummary({
          frequency: settings.dvla_check_frequency,
          dayOfMonth: settings.dvla_schedule_day_of_month ?? 1,
          time: settings.dvla_schedule_time || '06:00:00',
          timezone: settings.dvla_schedule_timezone || 'Europe/London',
          nextCheckDate: settings.next_dvla_check_date,
        })
      : null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        DVLA Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View your current DVLA configuration. Contact your administrator to update these settings.
      </Typography>

      <Grid container spacing={3}>
        {/* Credits Card - Clickable */}
        <Grid item xs={12} md={6}>
          <Card sx={{ position: 'relative' }}>
            <CardActionArea onClick={() => navigate('/admin/dvla-credit-usage')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreditCardIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">DVLA Credits</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Available check credits
                    </Typography>
                  </Box>
                  <VisibilityIcon sx={{ color: 'action.active' }} />
                </Box>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {settings?.dvla_credits || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total Credits
                </Typography>
                {pendingCredits > 0 && (
                  <>
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      {pendingCredits} pending (reserved)
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                      {(settings?.dvla_credits || 0) - pendingCredits} available
                    </Typography>
                  </>
                )}
                {pendingCredits === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {settings?.dvla_credits === 0 
                      ? 'No credits available. Contact your administrator to add credits.'
                      : `${settings?.dvla_credits} available`}
                  </Typography>
                )}
                <Button
                  variant="text"
                  endIcon={<VisibilityIcon />}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  View Usage Details
                </Button>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* Check Frequency Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">Automated Check Frequency</Typography>
                  <Typography variant="body2" color="text.secondary">
                    How often automated checks run
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Chip
                  label={settings?.dvla_check_frequency ? 'Scheduled' : 'Not set'}
                  color={getFrequencyColor(settings?.dvla_check_frequency || null)}
                  size="medium"
                />
              </Box>
              {scheduleSummary && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {scheduleSummary}
                </Typography>
              )}
              {!settings?.dvla_check_frequency && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Automated checks are not configured
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts: now centralised in Communications module */}
        <Grid item xs={12}>
          <Card sx={{ borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">DVLA Email & Push Alerts</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per-event rules in Communications → Alerts; legacy summary/alert email is set by your administrator.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  href="/admin/communications"
                  sx={{ ml: 2, whiteSpace: 'nowrap' }}
                >
                  Manage alerts
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create flexible rules for licence expiry, photocard expiry, points changes, new endorsements,
                or status changes. These work alongside the legacy notification email configured in business settings.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DVLASettings;

